/**
 * @file index.jsx
 * @author Rishikesh
 * @date 2024-09-11
 * @description Manage the course details.
 */
import { Box, Button, Skeleton, Stack } from "@mui/material";
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useSnackbar } from "notistack";

import { HeaderSection } from "./HeaderSection";
import { FormSection } from "./FormSection";
import { DetailsPanel } from "./DetailsPanel";
import { useFetchData } from "../../../../components/hooks/useFetchData";
import { LoadingBackdrop } from "../../../../components/common/LoadingBackdrop";

const ManageCourse = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const course = location.state?.course;
  const { enqueueSnackbar } = useSnackbar();
  const { fetchData, loading } = useFetchData();
  const [categories, setCategories] = useState([]);
  const [minLoading, setMinLoading] = useState(true);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm({
    defaultValues: {
      courseid: course?.id || 0,
      coursename: course?.coursename || "",
      category: course?.categoryid || null,
      isactive: course?.isactive || 0,
    },
  });

  const getCategories = async () => {
    const result = await fetchData("getcategories");
    if (result?.code === 200) {
      setCategories(result.data);
    }
  };

  const handleReset = () => {
    reset({
      courseid: course?.id || 0,
      coursename: course?.coursename || "",
      category: course?.categoryid || null,
      isactive: course?.isactive || 0,
    });
  };

  const onSubmit = async (data) => {
    const body = {
      courses: [
        {
          courseid: course?.id || 0,
          coursename: data.coursename,
          categoryid: data.category,
          isactive: data.isactive,
        },
      ],
    };
    const result = await fetchData("managecourse", {
      method: "post",
      body: body,
    });
    if (result?.code === 200) {
      enqueueSnackbar(result.message || "Successfull!", {
        variant: "success",
      });
      navigate(-1);
    } else {
      enqueueSnackbar(result.message || "Something went wrong!", {
        variant: "error",
      });
    }
  };

  React.useEffect(() => {
    getCategories();
    const minLoadingTimer = setTimeout(() => {
      setMinLoading(false);
    }, 1500);

    return () => clearTimeout(minLoadingTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isLoading = minLoading || loading["getcategories"];

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        width="100%"
        height="100%"
        overflow={{ xs: "auto", sm: "hidden" }}
      >
        <HeaderSection isLoading={isLoading} />
        <Stack
          pt={3}
          flex={1}
          overflow={{ xs: "unset", sm: "hidden" }}
          direction={{ xs: "column", sm: "row" }}
          columnGap={1}
          rowGap={2}
        >
          <FormSection
            isLoading={isLoading}
            categories={categories}
            register={register}
            errors={errors}
            setValue={setValue}
            watch={watch}
          />
          <DetailsPanel />
        </Stack>
        <Stack
          width="100%"
          mt={2}
          direction="row"
          alignItems="center"
          justifyContent="end"
          spacing={2}
        >
          {isLoading ? (
            <Skeleton variant="rounded" width={160} height={40} />
          ) : (
            <Button
              onClick={handleReset}
              variant="outlined"
              sx={{ borderRadius: "12px", minWidth: 160, py: 1.2 }}
            >
              Reset
            </Button>
          )}
          {isLoading ? (
            <Skeleton variant="rounded" width={160} height={40} />
          ) : (
            <Button
              onClick={handleSubmit(onSubmit)}
              variant="contained"
              sx={{ borderRadius: "12px", minWidth: 160, py: 1.2 }}
            >
              Save
            </Button>
          )}
        </Stack>
      </Box>
      <LoadingBackdrop isLoading={loading["managecourse"]} />
    </>
  );
};

export default ManageCourse;
